import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Users, Search, Download, UserPlus, Filter, MoreHorizontal,
  Shield, ChevronLeft, RefreshCw, FileText,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLOURS = {
  active: 'bg-green-100 text-green-800 border-green-300',
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  suspended: 'bg-orange-100 text-orange-800 border-orange-300',
  deactivated: 'bg-red-100 text-red-800 border-red-300',
  archived: 'bg-gray-200 text-gray-700 border-gray-300',
};

const ROLE_LABELS = {
  resident: 'Resident',
  trustee: 'Trustee',
  chairman: 'Chairman',
  property_manager: 'Property Manager',
  managing_agent: 'Managing Agent',
  agent_admin: 'Agent Admin',
  super_agent: 'Super Agent',
  general_agent: 'General Agent',
  platform_owner: 'Platform Owner',
};

const StatusBadge = ({ status }) => (
  <Badge
    data-testid={`user-status-${status}`}
    variant="outline"
    className={`${STATUS_COLOURS[status] || STATUS_COLOURS.active} capitalize font-medium`}
  >
    {status}
  </Badge>
);

const RoleBadge = ({ role }) => (
  <Badge variant="secondary" className="font-normal" data-testid={`user-role-${role}`}>
    {ROLE_LABELS[role] || role}
  </Badge>
);

const authHeaders = () => {
  const token = localStorage.getItem('session_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function UserManagement() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [hoaFilter, setHoaFilter] = useState('');

  // Selection
  const [selected, setSelected] = useState(new Set());

  // Modals
  const [showInvite, setShowInvite] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showRole, setShowRole] = useState(false);
  const [showReassign, setShowReassign] = useState(false);
  const [activeUser, setActiveUser] = useState(null);

  // Confirm dialog
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmReason, setConfirmReason] = useState('');

  // Hard-delete (purge) dialog — Platform Owner only
  const [purgeTarget, setPurgeTarget] = useState(null);
  const [purgeReason, setPurgeReason] = useState('');
  const [purgeConfirmEmail, setPurgeConfirmEmail] = useState('');

  const fetchStats = useCallback(async () => {
    const r = await fetch(`${API}/api/user-management/stats`, {
      credentials: 'include', headers: authHeaders(),
    });
    if (r.ok) setStats(await r.json());
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set('q', search);
    if (roleFilter !== 'all') params.set('role', roleFilter);
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (hoaFilter) params.set('hoa_id', hoaFilter);
    params.set('limit', '200');

    const r = await fetch(`${API}/api/user-management/users?${params}`, {
      credentials: 'include', headers: authHeaders(),
    });
    if (r.ok) {
      const data = await r.json();
      setUsers(data.users || []);
    } else if (r.status === 403) {
      toast.error('You do not have permission to view this page.');
      navigate('/');
    }
    setLoading(false);
  }, [search, roleFilter, statusFilter, hoaFilter, navigate]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => {
    const t = setTimeout(fetchUsers, 250); // debounce search
    return () => clearTimeout(t);
  }, [fetchUsers]);

  const callAction = async (path, body = {}) => {
    const r = await fetch(`${API}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(body),
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      // Continuity violation → return structured error so caller can prompt for replacement
      if (r.status === 409 && (err?.detail?.error === 'continuity_violation' ||
                                err?.detail?.error === 'primary_replacement_required')) {
        return { ok: false, continuity: err.detail };
      }
      toast.error(typeof err.detail === 'string' ? err.detail : (err.detail?.message || 'Action failed'));
      return { ok: false };
    }
    return { ok: true };
  };

  const handleStatusAction = (u, action) => {
    setActiveUser(u);
    setConfirmAction(action);
    setConfirmReason('');
  };

  const confirmStatusAction = async () => {
    if (!activeUser || !confirmAction) return;
    const result = await callAction(
      `/api/user-management/users/${activeUser.user_id}/${confirmAction}`,
      { reason: confirmReason },
    );
    if (result.ok) {
      toast.success(`User ${confirmAction}d`);
      fetchUsers();
      fetchStats();
      setActiveUser(null);
      setConfirmAction(null);
      setConfirmReason('');
    } else if (result.continuity) {
      // Show a toast + keep dialog open so the manager knows to act
      toast.error(result.continuity.message || 'Building continuity rule blocks this action.', {
        duration: 8000,
        description: 'Add or transfer to another admin first via the Building Admins screen.',
      });
    } else {
      setActiveUser(null);
      setConfirmAction(null);
      setConfirmReason('');
    }
  };

  const confirmPurge = async () => {
    if (!purgeTarget) return;
    if (purgeConfirmEmail.trim().toLowerCase() !== (purgeTarget.email || '').toLowerCase()) {
      toast.error('Type the user email exactly to confirm deletion.');
      return;
    }
    const r = await fetch(`${API}/api/user-management/users/${purgeTarget.user_id}/purge`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ reason: purgeReason }),
    });
    if (r.ok) {
      const data = await r.json();
      toast.success(data.message || 'User permanently deleted', { duration: 6000 });
      setPurgeTarget(null);
      setPurgeReason('');
      setPurgeConfirmEmail('');
      fetchUsers();
      fetchStats();
    } else {
      const err = await r.json().catch(() => ({}));
      const msg = typeof err.detail === 'string' ? err.detail : (err.detail?.message || 'Delete failed');
      toast.error(msg, { duration: 8000 });
    }
  };

  const handleBulk = async (action) => {
    if (selected.size === 0) {
      toast.error('Select at least one user');
      return;
    }
    if (!window.confirm(`Apply "${action}" to ${selected.size} user(s)?`)) return;
    const result = await callAction('/api/user-management/bulk-action', {
      user_ids: Array.from(selected),
      action,
    });
    if (result.ok) {
      toast.success(`Bulk ${action} complete`);
      setSelected(new Set());
      fetchUsers();
      fetchStats();
    }
  };

  const exportCsv = async () => {
    const params = new URLSearchParams();
    if (roleFilter !== 'all') params.set('role', roleFilter);
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (hoaFilter) params.set('hoa_id', hoaFilter);
    const r = await fetch(`${API}/api/user-management/export-csv?${params}`, {
      credentials: 'include', headers: authHeaders(),
    });
    if (!r.ok) return toast.error('Export failed');
    const blob = await r.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `bodycorp365-users-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const toggleSelectAll = () => {
    if (selected.size === users.length) setSelected(new Set());
    else setSelected(new Set(users.map((u) => u.user_id)));
  };

  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const canManage = useMemo(() => {
    const role = user?.role;
    return [
      'platform_owner', 'super_agent', 'agent_admin',
      'managing_agent', 'general_agent', 'trustee',
      'chairman', 'property_manager',
    ].includes(role);
  }, [user]);

  if (!canManage) {
    return (
      <div className="p-8 text-center">
        <Shield className="w-12 h-12 mx-auto text-gray-400 mb-4" />
        <h2 className="text-xl font-semibold">Access restricted</h2>
        <p className="text-gray-600">You need administrator privileges to manage users.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8" data-testid="user-management-page">
      {/* Header */}
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 flex items-center gap-3">
          <button
            onClick={() => {
              // Route to the correct dashboard for the actor's role instead of
              // relying on browser history (which is empty on direct navigation
              // and leaves the page stuck).
              if (user?.role === 'platform_owner') {
                navigate('/platform');
              } else if (['managing_agent', 'agent_admin', 'super_agent'].includes(user?.role)) {
                navigate('/agent');
              } else {
                navigate('/admin');
              }
            }}
            className="p-2 rounded hover:bg-gray-200"
            data-testid="back-button"
            aria-label="Back to dashboard"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
              <Users className="w-7 h-7 text-indigo-600" /> User Management
            </h1>
            <p className="text-gray-600 text-sm">Manage residents, trustees, agents and platform users.</p>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            <Card><CardContent className="p-4">
              <div className="text-xs text-gray-500">Total</div>
              <div className="text-2xl font-bold" data-testid="stat-total">{stats.total_users}</div>
            </CardContent></Card>
            {['active', 'pending', 'suspended', 'deactivated', 'archived'].map((s) => (
              <Card key={s}><CardContent className="p-4">
                <div className="text-xs text-gray-500 capitalize">{s}</div>
                <div className={`text-2xl font-bold`} data-testid={`stat-${s}`}>
                  {stats.by_status?.[s] || 0}
                </div>
              </CardContent></Card>
            ))}
          </div>
        )}

        {/* Toolbar */}
        <Card className="mb-4">
          <CardContent className="p-4 flex flex-col lg:flex-row gap-3 items-stretch lg:items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, email or unit number..."
                className="pl-9"
                data-testid="user-search-input"
              />
            </div>

            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-full lg:w-40" data-testid="role-filter">
                <SelectValue placeholder="Role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                {Object.entries(ROLE_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full lg:w-40" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
                <SelectItem value="deactivated">Deactivated</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>

            <Input
              value={hoaFilter}
              onChange={(e) => setHoaFilter(e.target.value)}
              placeholder="Building ID"
              className="w-full lg:w-40"
              data-testid="hoa-filter"
            />

            <Button variant="outline" onClick={fetchUsers} data-testid="refresh-button">
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
            <Button variant="outline" onClick={exportCsv} data-testid="export-csv-button">
              <Download className="w-4 h-4 mr-1" /> CSV
            </Button>
            <Button variant="outline" onClick={() => navigate(user?.role === 'platform_owner' ? '/platform/audit-log' : '/admin/audit-log')} data-testid="audit-log-button">
              <FileText className="w-4 h-4 mr-1" /> Audit Log
            </Button>
            <Button onClick={() => setShowInvite(true)} data-testid="invite-user-button">
              <UserPlus className="w-4 h-4 mr-1" /> Invite User
            </Button>
          </CardContent>
        </Card>

        {/* Bulk-actions strip */}
        {selected.size > 0 && (
          <div className="mb-3 p-3 bg-indigo-50 border border-indigo-200 rounded-lg flex flex-wrap items-center gap-2" data-testid="bulk-actions-bar">
            <span className="text-sm font-medium">{selected.size} selected</span>
            <div className="flex-1" />
            <Button size="sm" variant="outline" onClick={() => handleBulk('suspend')} data-testid="bulk-suspend">Suspend</Button>
            <Button size="sm" variant="outline" onClick={() => handleBulk('deactivate')} data-testid="bulk-deactivate">Deactivate</Button>
            <Button size="sm" variant="outline" onClick={() => handleBulk('archive')} data-testid="bulk-archive">Archive</Button>
            <Button size="sm" variant="outline" onClick={() => handleBulk('restore')} data-testid="bulk-restore">Restore</Button>
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Clear</Button>
          </div>
        )}

        {/* Table */}
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={selected.size > 0 && selected.size === users.length}
                      onCheckedChange={toggleSelectAll}
                      data-testid="select-all-checkbox"
                    />
                  </TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Building</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead>Last Login</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={10} className="text-center py-8">Loading…</TableCell></TableRow>
                ) : users.length === 0 ? (
                  <TableRow><TableCell colSpan={10} className="text-center py-8 text-gray-500">No users found.</TableCell></TableRow>
                ) : users.map((u) => (
                  <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                    <TableCell>
                      <Checkbox
                        checked={selected.has(u.user_id)}
                        onCheckedChange={() => toggleOne(u.user_id)}
                        data-testid={`row-checkbox-${u.user_id}`}
                      />
                    </TableCell>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell className="text-sm text-gray-600">{u.email}</TableCell>
                    <TableCell><RoleBadge role={u.role} /></TableCell>
                    <TableCell className="text-sm">{u.hoa_name || u.hoa_id || '—'}</TableCell>
                    <TableCell className="text-sm">{u.unit_number || '—'}</TableCell>
                    <TableCell><StatusBadge status={u.status} /></TableCell>
                    <TableCell className="text-xs text-gray-500">{(u.created_at || '').slice(0, 10)}</TableCell>
                    <TableCell className="text-xs text-gray-500">{(u.last_login || '').slice(0, 10) || '—'}</TableCell>
                    <TableCell className="text-right">
                      <RowActions
                        u={u}
                        isPlatformOwner={user?.role === 'platform_owner'}
                        onEdit={() => { setActiveUser(u); setShowEdit(true); }}
                        onRole={() => { setActiveUser(u); setShowRole(true); }}
                        onReassign={() => { setActiveUser(u); setShowReassign(true); }}
                        onStatus={(act) => handleStatusAction(u, act)}
                        onPurge={() => { setPurgeTarget(u); setPurgeReason(''); setPurgeConfirmEmail(''); }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Invite Modal */}
      <InviteUserDialog
        open={showInvite}
        onOpenChange={setShowInvite}
        onSuccess={() => { fetchUsers(); fetchStats(); }}
      />

      {/* Edit Modal */}
      <EditUserDialog
        open={showEdit}
        onOpenChange={setShowEdit}
        target={activeUser}
        onSuccess={() => { fetchUsers(); }}
      />

      {/* Role Modal */}
      <ChangeRoleDialog
        open={showRole}
        onOpenChange={setShowRole}
        target={activeUser}
        onSuccess={() => { fetchUsers(); }}
      />

      {/* Reassign Unit Modal */}
      <ReassignUnitDialog
        open={showReassign}
        onOpenChange={setShowReassign}
        target={activeUser}
        onSuccess={() => { fetchUsers(); fetchStats(); }}
      />

      {/* Confirmation for status changes */}
      <AlertDialog open={!!confirmAction} onOpenChange={(o) => !o && setConfirmAction(null)}>
        <AlertDialogContent data-testid="confirm-status-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="capitalize">{confirmAction} user?</AlertDialogTitle>
            <AlertDialogDescription>
              {activeUser?.email} — This will {confirmAction === 'restore' ? 'restore access for' : `${confirmAction} access for`} this user.
              Historical records remain intact.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label>Reason (optional)</Label>
            <Input
              value={confirmReason}
              onChange={(e) => setConfirmReason(e.target.value)}
              placeholder="e.g. resident moved out"
              data-testid="confirm-reason-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmStatusAction} data-testid="confirm-action">Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Hard-delete (purge) — Platform Owner only */}
      <AlertDialog open={!!purgeTarget} onOpenChange={(o) => !o && setPurgeTarget(null)}>
        <AlertDialogContent data-testid="purge-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-700">Permanently delete this user?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{purgeTarget?.email}</strong> will be permanently removed. The email
              address will be freed and can be used to create a new account
              (e.g. when a resident moves to a new building).
              <br /><br />
              <span className="text-red-700 font-medium">
                This cannot be undone.
              </span> Historical records
              (audit logs, votes, maintenance tickets) remain intact for compliance.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Type <span className="font-mono">{purgeTarget?.email}</span> to confirm</Label>
              <Input
                value={purgeConfirmEmail}
                onChange={(e) => setPurgeConfirmEmail(e.target.value)}
                placeholder={purgeTarget?.email || ''}
                data-testid="purge-confirm-email"
              />
            </div>
            <div>
              <Label className="text-xs">Reason (optional)</Label>
              <Input
                value={purgeReason}
                onChange={(e) => setPurgeReason(e.target.value)}
                placeholder="e.g. resident moved out, wants to re-register"
                data-testid="purge-reason"
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="purge-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmPurge}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              data-testid="purge-confirm"
            >
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ============================================================
// Row Actions Menu
// ============================================================
function RowActions({ u, onEdit, onRole, onReassign, onStatus, onPurge, isPlatformOwner }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative inline-block text-left">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen((o) => !o)}
        data-testid={`row-actions-${u.user_id}`}
      >
        <MoreHorizontal className="w-4 h-4" />
      </Button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 w-48 bg-white border rounded-md shadow-lg z-20 py-1 text-sm">
            <button className="w-full text-left px-3 py-2 hover:bg-gray-50" onClick={() => { onEdit(); setOpen(false); }} data-testid={`edit-${u.user_id}`}>Edit</button>
            <button className="w-full text-left px-3 py-2 hover:bg-gray-50" onClick={() => { onRole(); setOpen(false); }} data-testid={`change-role-${u.user_id}`}>Change role</button>
            <button className="w-full text-left px-3 py-2 hover:bg-gray-50" onClick={() => { onReassign(); setOpen(false); }} data-testid={`reassign-${u.user_id}`}>Reassign unit</button>
            <div className="border-t my-1" />
            {u.status === 'active' && (
              <>
                <button className="w-full text-left px-3 py-2 hover:bg-orange-50 text-orange-700" onClick={() => { onStatus('suspend'); setOpen(false); }} data-testid={`suspend-${u.user_id}`}>Suspend</button>
                <button className="w-full text-left px-3 py-2 hover:bg-red-50 text-red-700" onClick={() => { onStatus('deactivate'); setOpen(false); }} data-testid={`deactivate-${u.user_id}`}>Deactivate</button>
              </>
            )}
            {(u.status === 'suspended' || u.status === 'deactivated') && (
              <button className="w-full text-left px-3 py-2 hover:bg-green-50 text-green-700" onClick={() => { onStatus('restore'); setOpen(false); }} data-testid={`restore-${u.user_id}`}>Restore</button>
            )}
            <button className="w-full text-left px-3 py-2 hover:bg-gray-50 text-gray-700" onClick={() => { onStatus('archive'); setOpen(false); }} data-testid={`delete-${u.user_id}`}>Delete (soft)</button>
            {isPlatformOwner && (
              <>
                <div className="border-t my-1" />
                <button
                  className="w-full text-left px-3 py-2 hover:bg-red-50 text-red-700 font-medium"
                  onClick={() => { onPurge(); setOpen(false); }}
                  data-testid={`purge-${u.user_id}`}
                >
                  Delete permanently
                </button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================
// Invite Dialog
// ============================================================
function InviteUserDialog({ open, onOpenChange, onSuccess }) {
  const [form, setForm] = useState({ email: '', name: '', role: 'resident', hoa_id: '', unit_number: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!form.email || !form.name) return toast.error('Email and name are required');
    setSubmitting(true);
    const r = await fetch(`${API}/api/user-management/invite`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(form),
    });
    if (r.ok) {
      toast.success('Invitation sent');
      onOpenChange(false);
      setForm({ email: '', name: '', role: 'resident', hoa_id: '', unit_number: '', phone: '' });
      onSuccess();
    } else {
      const e = await r.json().catch(() => ({}));
      toast.error(e.detail || 'Failed to invite');
    }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="invite-user-dialog">
        <DialogHeader>
          <DialogTitle>Invite a new user</DialogTitle>
          <DialogDescription>They'll receive an email invitation.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Email *</Label>
            <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="invite-email" />
          </div>
          <div>
            <Label>Full name *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="invite-name" />
          </div>
          <div>
            <Label>Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(ROLE_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Building ID</Label>
              <Input value={form.hoa_id} onChange={(e) => setForm({ ...form, hoa_id: e.target.value })} data-testid="invite-hoa" />
            </div>
            <div>
              <Label>Unit Number</Label>
              <Input value={form.unit_number} onChange={(e) => setForm({ ...form, unit_number: e.target.value })} data-testid="invite-unit" />
            </div>
          </div>
          <div>
            <Label>Phone</Label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="invite-phone" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="invite-submit">
            {submitting ? 'Sending…' : 'Send Invite'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// Edit Dialog
// ============================================================
function EditUserDialog({ open, onOpenChange, target, onSuccess }) {
  const [form, setForm] = useState({});
  useEffect(() => {
    if (target) setForm({ name: target.name || '', phone: target.phone || '', unit_number: target.unit_number || '' });
  }, [target]);

  const submit = async () => {
    if (!target) return;
    const r = await fetch(`${API}/api/user-management/users/${target.user_id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(form),
    });
    if (r.ok) {
      toast.success('User updated');
      onOpenChange(false);
      onSuccess();
    } else {
      const e = await r.json().catch(() => ({}));
      toast.error(e.detail || 'Update failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-user-dialog">
        <DialogHeader>
          <DialogTitle>Edit user</DialogTitle>
          <DialogDescription>{target?.email}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Full name</Label><Input value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-name" /></div>
          <div><Label>Phone</Label><Input value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="edit-phone" /></div>
          <div><Label>Unit Number</Label><Input value={form.unit_number || ''} onChange={(e) => setForm({ ...form, unit_number: e.target.value })} data-testid="edit-unit" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} data-testid="edit-submit">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// Change Role Dialog
// ============================================================
function ChangeRoleDialog({ open, onOpenChange, target, onSuccess }) {
  const [newRole, setNewRole] = useState('resident');
  const [reason, setReason] = useState('');
  useEffect(() => { if (target) setNewRole(target.role || 'resident'); }, [target]);

  const submit = async () => {
    if (!target) return;
    const r = await fetch(`${API}/api/user-management/users/${target.user_id}/change-role`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ new_role: newRole, reason }),
    });
    if (r.ok) {
      toast.success('Role updated');
      onOpenChange(false);
      onSuccess();
    } else {
      const e = await r.json().catch(() => ({}));
      toast.error(e.detail || 'Role change failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="role-dialog">
        <DialogHeader>
          <DialogTitle>Change role</DialogTitle>
          <DialogDescription>{target?.email} — current: <strong>{target?.role}</strong></DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>New role</Label>
            <Select value={newRole} onValueChange={setNewRole}>
              <SelectTrigger data-testid="role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(ROLE_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Reason</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="role-reason" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} data-testid="role-submit">Update Role</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// Reassign Unit Dialog
// ============================================================
function ReassignUnitDialog({ open, onOpenChange, target, onSuccess }) {
  const [form, setForm] = useState({ new_user_email: '', unit_number: '', reason: '' });
  useEffect(() => { if (target) setForm((f) => ({ ...f, unit_number: target.unit_number || '' })); }, [target]);

  const submit = async () => {
    if (!target) return;
    if (!form.new_user_email) return toast.error('New resident email is required');
    const r = await fetch(`${API}/api/user-management/users/${target.user_id}/reassign-unit`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(form),
    });
    if (r.ok) {
      toast.success('Unit reassigned. Old resident deactivated; history retained.');
      onOpenChange(false);
      onSuccess();
    } else {
      const e = await r.json().catch(() => ({}));
      toast.error(e.detail || 'Reassign failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="reassign-dialog">
        <DialogHeader>
          <DialogTitle>Reassign unit</DialogTitle>
          <DialogDescription>
            Deactivate <strong>{target?.email}</strong> and assign their unit to a new resident.
            Historical records (claims, votes, tickets) remain intact.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>New resident email *</Label><Input value={form.new_user_email} onChange={(e) => setForm({ ...form, new_user_email: e.target.value })} data-testid="reassign-email" /></div>
          <div><Label>Unit number</Label><Input value={form.unit_number} onChange={(e) => setForm({ ...form, unit_number: e.target.value })} data-testid="reassign-unit-num" /></div>
          <div><Label>Reason</Label><Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="e.g. property sold" data-testid="reassign-reason" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} data-testid="reassign-submit">Reassign</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
